import {
  PaginatorModel,
  defaultPaginatorModelValues
} from "components/Paginator";
import useCaptureReportAPI, {
  CaptureReport,
  ListCaptureReportParams,
  DownloadCaptureReportParams,
  VehiclesMake
} from "api/CaptureReportAPI";
import {
  parseClassifierCoordinate,
  parsePlateCoordinate
} from "components/ImageViewer";
import Pages from "enums/Pages";
import useToolsAPI from "api/ToolsAPI";
import { EMPTY_VALUE } from "utils/String";
import fileDownload from "js-file-download";
import LinkPlate from "components/LinkPlate";
import DataTable from "components/DataTable";
import { useIsMount } from "hooks/useIsMount";
import { useTranslation } from "react-i18next";
import { useAuth } from "contexts/AuthContext";
import CaptureReportFilter, {
  FormFilterCaptures
} from "pages/CaptureReport/CaptureReportFilter";
import { cepFormatter } from "utils/cepFormatter";
import { useLocale } from "contexts/LocaleContext";
import CustomFilterKeys from "enums/CustomFilterKeys";
import InnerPageLayout from "layouts/InnerPageLayout";
import { Download, Map, Sliders } from "react-feather";
import { useHistory, useParams } from "react-router-dom";
import DefaultPageLayout from "layouts/DefaultPageLayout";
import useCameraAPI, { CameraFilter } from "api/CameraAPI";
import useLocationAPI, { Location } from "api/LocationAPI";
import CaptureImgButton from "components/CaptureImgButton";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import snackNotification from "components/SnackNotification";
import PageSection from "components/PageSection/PageSection";
import { useErrorHandler } from "contexts/ErrorHandlerContext";
import { Box, CircularProgress, Typography } from "@mui/material";
import { dateTimeToString, dateToString } from "utils/DateFunctions";
import CaptureImageViewerDialog, {
  CaptureImageDialog,
  initialCaptureImageDialogProps
} from "components/CaptureImageViewerDialog/CaptureImageViewerDialog";
import { endOfDay, set, startOfDay } from "date-fns";
import HeatmapPlateDialog from "pages/CaptureReport/HeatmapPlateDialog";
import { captureReportTableColumns } from "utils/captureReportTableColumns";
import { LocationSection, usePageLocation } from "contexts/PageLocationContext";
import PageSectionHeaderAction from "components/PageSection/PageSectionHeaderAction";
import { CustomFilterProvider } from "components/CustomFilterMenu/CustomFilterContext";
import DownloadCaptureReportDialog from "pages/CaptureReport/DownloadCaptureReportDialog";
import { FormDownloadCaptureReport } from "pages/CaptureReport/DownloadCaptureReportDialog";
import { LocationFilter } from "api/LocationAPI";

const CaptureReportPage: FC = () => {
  const params = useParams<{ plate: string }>();
  const plate = params.plate ? decodeURIComponent(params.plate) : "";
  const defaultDate = new Date();
  const defaultFilterValues: FormFilterCaptures = {
    id: "",
    equipments: [],
    cameras: [],
    direction: "",
    street: "",
    number: "",
    city: "",
    state: "",
    zip_code: "",
    vehicleClasses: [],
    plate: "",
    startDate: set(defaultDate, { hours: 0, minutes: 0, seconds: 0 }),
    endDate: defaultDate,
    startTime: startOfDay(defaultDate),
    endTime: endOfDay(defaultDate),
    make: "",
    model: "",
    color: ""
  };
  const [filter, setFilter] = useState<FormFilterCaptures>(defaultFilterValues);
  const [isFilterOpen, setFilterOpen] = useState(!params.plate);
  const [isDownloadCaptureReportOpen, setDownloadCaptureReportOpen] =
    useState(false);
  const [heatmapOpen, setHeatmapOpen] = useState(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isDownloadingCsv, setDownloadingCsv] = useState<boolean>(false);
  const [isDownloadingPdf, setDownloadingPdf] = useState<boolean>(false);
  const [reportResults, setReportResults] = useState<CaptureReport[]>([]);
  const [page, setPage] = useState(1);
  const [paginator, setPaginator] = useState<PaginatorModel>(
    defaultPaginatorModelValues
  );
  const { sessionUser } = useAuth();
  const { language } = useLocale();
  const LocationAPI = useLocationAPI();
  const ToolsAPI = useToolsAPI();
  const CaptureReportAPI = useCaptureReportAPI();
  const CameraAPI = useCameraAPI();
  const { t } = useTranslation();
  const { errorHandler } = useErrorHandler();
  const [cameras, setCameras] = useState<CameraFilter[]>([]);
  const [equipments, setEquipments] = useState<Location[]>([]);
  const [locationsName, setlocationsName] = useState<string[]>([]);
  const [locationsComments, setLocationsComments] = useState<LocationFilter[]>(
    []
  );
  const [makes, setMakes] = useState<VehiclesMake[]>([]);

  useEffect(() => {
    (async () => {
      if (sessionUser) {
        const customerId = sessionUser["customer_id"];
        try {
          const [locationResponse, cameraResponse, makeResponse] =
            await Promise.all([
              LocationAPI.listAll(customerId),
              CameraAPI.listAll({ customerId }),
              ToolsAPI.listAllMakes()
            ]);

          const liteMake = makeResponse.make
            .filter(item => item.lite === true)
            .map(item => ({ name: item.name, isDivider: false }));
          liteMake[liteMake.length - 1].isDivider = true;
          const nonLiteMake = makeResponse.make
            .filter(item => item.lite === false)
            .map(item => ({ name: item.name, isDivider: false }));
          const makes = liteMake.concat(nonLiteMake);

          setMakes(makes || []);
          setCameras([
            t("form.all"),
            ...cameraResponse.data.map(camera => ({
              ["location_name"]: camera["location_name"],
              ["camera_name"]: camera["camera_name"]
            }))
          ]);
          setlocationsName([
            t("form.all"),
            ...locationResponse.data.map(location => location["location_name"])
          ]);
          setLocationsComments([
            t("form.all"),
            ...locationResponse.data.map(location => ({
              ["location_name"]: location["location_name"],
              comment: location["location_data"].comment
            }))
          ]);
          setEquipments(locationResponse.data);
        } catch (error) {
          errorHandler({ error });
        }
      }
    })();
  }, []);

  const defaultDownloadValues: FormDownloadCaptureReport = {
    formatType: "",
    tableColumnsSelected: []
  };

  const [dialogDownload, setDialogDownload] =
    useState<FormDownloadCaptureReport>({
      ...defaultDownloadValues
    });

  const history = useHistory();

  // sinaliza que a URL /plate foi gerada por um filtro ja completo (com datas,
  // number_occurrences etc.), pro efeito de inicializacao nao resetar o filtro
  // pra "so placa, sem data" logo em seguida
  const plateUrlFromFilterRef = useRef(false);

  const requestData = useCallback(
    async (
      filterParam: FormFilterCaptures,
      pageValue: number,
      pageSizeValue: number
    ) => {
      if (!sessionUser?.["customer_id"]) return;
      const customerId = sessionUser["customer_id"];
      setLoading(true);
      try {
        if (!filterParam.plate || filterParam.plate.length < 1) {
          history.replace(Pages.CAPTURE_REPORT);
        } else if (!plate && filterParam.plate) {
          plateUrlFromFilterRef.current = true;
          history.replace(
            `${Pages.CAPTURE_REPORT}/${encodeURIComponent(filterParam.plate)}`
          );
        }

        let listFilterParam: ListCaptureReportParams = {
          ["customer_id"]: customerId,
          page: pageValue,
          ["page_size"]: pageSizeValue
        };

        listFilterParam = {
          ...listFilterParam,
          ["basic_filter"]: {
            ["initial_date"]: filterParam.startDate
              ? dateTimeToString(
                  filterParam.startDate as Date,
                  filterParam.startTime as Date
                )
              : undefined,
            ["final_date"]: filterParam.endDate
              ? dateTimeToString(
                  filterParam.endDate as Date,
                  filterParam.endTime as Date
                )
              : undefined,
            plate: filterParam.plate === "" ? undefined : filterParam.plate,
            ["id_capture"]: filterParam.id === "" ? undefined : filterParam.id,
            equipments:
              filterParam.equipments && filterParam.equipments?.length > 0
                ? filterParam.equipments
                : undefined,
            cameras:
              filterParam.cameras && filterParam.cameras.length > 0
                ? filterParam.cameras
                : undefined,
            ["vehicle_make"]:
              filterParam.make === "" ? undefined : filterParam.make,
            ["vehicle_model"]:
              filterParam.model === "" ? undefined : filterParam.model,
            ["vehicle_color"]:
              filterParam.color === "" ? undefined : filterParam.color,
            ["vehicle_classes"]:
              filterParam.vehicleClasses &&
              filterParam.vehicleClasses.length === 0
                ? undefined
                : filterParam.vehicleClasses,
            ["number_occurrences"]: filterParam.numberOccurrences
          }
        };

        const reportResponse = await CaptureReportAPI.list(listFilterParam);

        setReportResults(reportResponse.registers.items || []);

        setPaginator({
          totalPages:
            Math.ceil(reportResponse.registers.total_items / pageSizeValue) ||
            0,
          totalItems: reportResponse.registers.total_items || 0
        });
        setPage(reportResponse.registers.page || 1);
      } catch (error) {
        errorHandler({ error });
      } finally {
        setLoading(false);
      }
    },
    [sessionUser]
  );

  const onDialogDownload = (dialogDownloadValue: FormDownloadCaptureReport) => {
    setDialogDownload(dialogDownloadValue);
  };

  const isMount = useIsMount();

  useEffect(() => {
    if (!isMount) {
      if (dialogDownload.formatType === "CSV") {
        downloadCsv();
      } else if (dialogDownload.formatType === "PDF") {
        downloadPdf();
      }
    }
  }, [dialogDownload]);

  const getDownloadReportParams = (
    filter: FormFilterCaptures
  ): DownloadCaptureReportParams => {
    let customerId = "";
    if (sessionUser) customerId = sessionUser["customer_id"];

    let downloadParams: DownloadCaptureReportParams = {
      ["customer_id"]: customerId,
      language: language.split("-")[0],
      ["columns_selected"]: dialogDownload.tableColumnsSelected
    };
    downloadParams = {
      ...downloadParams,
      ["basic_filter"]: {
        ["initial_date"]: filter.startDate
          ? dateTimeToString(filter.startDate as Date, filter.startTime as Date)
          : undefined,
        ["final_date"]: filter.endDate
          ? dateTimeToString(filter.endDate as Date, filter.endTime as Date)
          : undefined,
        plate: filter.plate === "" ? undefined : filter.plate,
        ["id_capture"]: filter.id || undefined,
        cameras: filter.cameras?.length ? filter.cameras : undefined,
        equipments: filter.equipments?.length ? filter.equipments : undefined,
        ["vehicle_make"]: filter.make === "" ? undefined : filter.make,
        ["vehicle_model"]: filter.model === "" ? undefined : filter.model,
        ["vehicle_color"]: filter.color === "" ? undefined : filter.color,
        ["number_occurrences"]: filter.numberOccurrences?.length
          ? filter.numberOccurrences
          : undefined
      }
    };
    return downloadParams;
  };

  const downloadCsv = async () => {
    if (!filter || isDownloadingCsv || !sessionUser?.["customer_id"]) return;
    try {
      setDownloadingCsv(true);
      const response = await CaptureReportAPI.downloadCsv(
        getDownloadReportParams(filter)
      );
      fileDownload(response, "reportRegisters.csv", "text/csv");
      snackNotification.success(t("CaptureReportPage.downloadedSuccess"));
    } catch (error) {
      errorHandler({ error });
    } finally {
      setDownloadingCsv(false);
    }
  };

  const forceDownloadPdf = (pdf: string) => {
    const linkSource = `data:application/pdf;base64,${pdf}`;
    const downloadLink = document.createElement("a");
    const fileName = "reportRegisters.pdf";

    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  };

  const downloadPdf = async () => {
    if (!filter || isDownloadingPdf || !sessionUser?.["customer_id"]) return;
    try {
      setDownloadingPdf(true);
      const response = await CaptureReportAPI.downloadPdf(
        getDownloadReportParams(filter)
      );
      forceDownloadPdf(response);
      snackNotification.success(t("CaptureReportPage.downloadedSuccess"));
    } catch (error) {
      errorHandler({ error });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const [capture, setCapture] = useState<CaptureImageDialog>(
    initialCaptureImageDialogProps
  );
  const [isCaptureDialogOpen, setCaptureDialogOpen] = useState<boolean>(false);

  const getCaptureReportCoord = (capture: CaptureReport) => {
    // Check CaptureReport first
    if (capture.latitude && capture.longitude) {
      return { latitude: capture.latitude, longitude: capture.longitude };
    }
    // Search equipments list
    const coord = equipments.find(
      item => item?.["location_name"] === capture?.equipment
    );
    return {
      latitude: coord?.["location_data"]["latitude"] ?? "",
      longitude: coord?.["location_data"]["longitude"] ?? ""
    };
  };

  const openCaptureDialog = (capture: CaptureReport) => {
    const coord = getCaptureReportCoord(capture);
    const vehicleCategory = t(
      `VehicleOverviewPage.${
        capture.plate === "0000000" ? "unknown" : capture.vehicle_class
      }`
    );
    setCapture({
      id: capture.id,
      image: capture.image_link,
      equipment: capture.equipment,
      camera: capture.camera,
      dateTime: capture["date_capture"],
      plate: capture.plate,
      plateCoordinate: capture.plate_coordinate
        ? parsePlateCoordinate(capture.plate_coordinate)
        : undefined,
      ["classifier_list"]: capture.classifier_list
        ? parseClassifierCoordinate(capture.classifier_list)
        : undefined,
      longitude: coord["longitude"],
      latitude: coord["latitude"],
      renavam: {
        vehicleMake: capture.vehicle_make,
        vehicleModel: capture.vehicle_model,
        vehicleColor: capture.vehicle_color,
        vehicleCategory: vehicleCategory.toUpperCase(),
        vehicleYear: capture.vehicle_year
      }
    });
    setCaptureDialogOpen(true);
  };

  const { setPageTitle, setLocation } = usePageLocation();

  useEffect(() => {
    const title = plate
      ? t("windowTitle.captureReportPlate", { plate })
      : t("windowTitle.captureReport");
    setPageTitle(title);
    const location: LocationSection[] = [
      {
        label: t("menu.reports")
      },
      {
        label: t("CaptureReportPage.title"),
        page: Pages.CAPTURE_REPORT
      }
    ];
    setReportResults([]);
    setPaginator(defaultPaginatorModelValues);
    setPage(1);
    if (params.plate && plate.length > 0) {
      if (plateUrlFromFilterRef.current) {
        // requestData ja buscou com o filtro completo (datas, number_occurrences...)
        // antes de trocar a URL; nao refazer a busca nem resetar o filtro aqui.
        plateUrlFromFilterRef.current = false;
      } else {
        requestData({ id: "", plate }, page, pageSize);
        // busca via URL /capture-report/:plate e so por placa, sem range de data - herdar
        // o filter state (que tem startDate/endDate default de hoje) faria as PROXIMAS
        // paginas passarem a filtrar por hoje sem o cliente ter pedido isso.
        setFilter({
          ...defaultFilterValues,
          plate,
          startDate: null,
          endDate: null
        });
      }
      location.push({
        label: plate,
        page: `${Pages.CAPTURE_REPORT}/${encodeURIComponent(plate)}`
      });
    } else {
      setFilter(defaultFilterValues);
    }
    setLocation(location);
  }, [t, plate]);

  const onFilter = (filterValue: FormFilterCaptures) => {
    setFilter(filterValue);
    setPage(1);
    requestData(filterValue, 1, pageSize);
  };

  return (
    <DefaultPageLayout>
      <InnerPageLayout>
        <PageSection
          title={t("CaptureReportPage.title")}
          isLoading={isLoading}
          actions={
            <>
              {plate && (
                <PageSectionHeaderAction
                  startIcon={<Map />}
                  onClick={() => setHeatmapOpen(true)}
                >
                  {t("DefaultPageLayout.map")}
                </PageSectionHeaderAction>
              )}
              <>
                <PageSectionHeaderAction
                  variant="outlined"
                  disabled={paginator.totalItems < 1}
                  color="primary"
                  startIcon={
                    isDownloadingCsv || isDownloadingPdf ? (
                      <CircularProgress size={22} color="secondary" />
                    ) : (
                      <Download />
                    )
                  }
                  onClick={() => setDownloadCaptureReportOpen(true)}
                >
                  {t("action.export")}
                </PageSectionHeaderAction>
              </>
            </>
          }
          primaryActions={
            <>
              <PageSectionHeaderAction
                variant="contained"
                color="secondary"
                startIcon={<Sliders />}
                onClick={() => setFilterOpen(true)}
              >
                {t("action.filter")}
              </PageSectionHeaderAction>
            </>
          }
        >
          <CustomFilterProvider filterKey={CustomFilterKeys.CAPTURE_REPORT}>
            <CaptureReportFilter
              open={isFilterOpen}
              cameras={cameras}
              locationsName={locationsName}
              locationsComments={locationsComments}
              makes={makes}
              setOpen={setFilterOpen}
              filter={filter}
              defaultValues={defaultFilterValues}
              onFilter={onFilter}
            />
          </CustomFilterProvider>
          <Box>
            <DataTable
              watermarked
              headers={[
                {
                  key: captureReportTableColumns[0].key,
                  label: t(
                    `CaptureReportPage.${captureReportTableColumns[0].label}`
                  )
                },
                {
                  key: captureReportTableColumns[1].key,
                  label: t(
                    `CaptureReportPage.${captureReportTableColumns[1].label}`
                  )
                },
                {
                  key: captureReportTableColumns[2].key,
                  label: t(
                    `CaptureReportPage.${captureReportTableColumns[2].label}`
                  )
                },
                {
                  key: captureReportTableColumns[3].key,
                  label: t(
                    `CaptureReportPage.${captureReportTableColumns[3].label}`
                  )
                },
                {
                  key: captureReportTableColumns[4].key,
                  label: t(
                    `CaptureReportPage.${captureReportTableColumns[4].label}`
                  )
                },
                {
                  key: captureReportTableColumns[5].key,
                  label: t(
                    `CaptureReportPage.${captureReportTableColumns[5].label}`
                  )
                },
                {
                  key: captureReportTableColumns[6].key,
                  label: t(
                    `CaptureReportPage.${captureReportTableColumns[6].label}`
                  )
                },
                {
                  key: captureReportTableColumns[7].key,
                  label: t(
                    `CaptureReportPage.${captureReportTableColumns[7].label}`
                  )
                },
                {
                  key: captureReportTableColumns[8].key,
                  label: t(
                    `CaptureReportPage.${captureReportTableColumns[8].label}`
                  )
                },
                {
                  key: "capture",
                  label: t("CaptureReportPage.capture"),
                  noSort: true
                }
              ]}
              defaultSort={["id", "asc"]}
              onHeaderSort={setReportResults}
              data={reportResults}
              renderRow={row => [
                <>{row.id}</>,
                <>
                  {dateToString(
                    row["date_capture"],
                    t("CaptureReportPage.dateFormat")
                  )}
                </>,
                <>{row.equipment}</>,
                <>{row.camera}</>,
                <>{t(`CameraDirection.${row.side}`)}</>,
                <>
                  {(row.state === "" && row.city === "") ||
                  (row.state === "ND" && row.city === "ND") ? (
                    <Typography component="div" variant="caption">
                      {t("EquipmentsPage.noAddress")}
                    </Typography>
                  ) : (
                    <>
                      <Typography component="div" variant="caption">
                        <strong>{`${row.street}, ${row.number}`}</strong>
                      </Typography>
                      <Typography color="textSecondary" variant="caption">
                        {`${cepFormatter(row.zip_code)},
                          ${row.city} - ${row.state}`}
                      </Typography>
                    </>
                  )}
                </>,
                <>
                  <LinkPlate plate={row.plate} path={Pages.VEHICLE_OVERVIEW} />
                </>,
                <>
                  <div>{row["vehicle_make"] || EMPTY_VALUE}</div>
                  <div>{row["vehicle_model"] || EMPTY_VALUE}</div>
                  <div>{row["vehicle_color"] || EMPTY_VALUE}</div>
                </>,
                <>
                  {t(
                    `MonitoredVehiclesDashboardPage.${
                      row.plate && row.plate === "0000000"
                        ? "unknown"
                        : row.vehicle_class
                    }`
                  )}
                </>,
                <>
                  <CaptureImgButton
                    image_link={row.image_link}
                    onClick={() => openCaptureDialog(row)}
                  />
                </>
              ]}
              hideColumnsSm={[5, 6]}
              hideColumnsXs={[0, 2, 3, 5, 6]}
              page={page}
              onPageChange={pageValue =>
                requestData(filter, pageValue, pageSize)
              }
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalPages={paginator.totalPages}
              totalItems={paginator.totalItems}
              isLoading={isLoading}
            />
          </Box>
        </PageSection>
      </InnerPageLayout>
      <CaptureImageViewerDialog
        open={isCaptureDialogOpen}
        setOpen={setCaptureDialogOpen}
        capture={capture}
        setCapture={(capture: CaptureImageDialog) => {
          setCapture(capture);
          setReportResults(
            reportResults.map(report =>
              report.id === capture.id
                ? { ...report, plate: capture.plate }
                : report
            )
          );
        }}
      />
      <HeatmapPlateDialog
        open={heatmapOpen}
        setOpen={setHeatmapOpen}
        filterData={filter}
      />
      <DownloadCaptureReportDialog
        open={isDownloadCaptureReportOpen}
        download={defaultDownloadValues}
        setOpen={setDownloadCaptureReportOpen}
        defaultValues={defaultDownloadValues}
        onDialogDownload={onDialogDownload}
      />
    </DefaultPageLayout>
  );
};

export default CaptureReportPage;
